import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { isVerified } from "@/lib/auth";
import { isAllowlisted } from "@/lib/allowlist";
import { joinWaitlist } from "@/lib/waitlist";

export type Access = "out" | "in" | "pending";
type Ctx = { user: User | null; access: Access; corp: boolean; loading: boolean };
type State = { user: User | null; access: Access; loading: boolean };
const AuthCtx = createContext<Ctx>({ user: null, access: "out", corp: false, loading: true });
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<State>({ user: null, access: "out", loading: true });
  useEffect(() => {
    // Sequence guard: the allowlist check is async, so a newer auth event (sign-out, account
    // switch) must supersede a slower in-flight check AND its fire-and-forget join.
    let seq = 0;
    return onAuthStateChanged(auth, (u) => {
      const my = ++seq;
      if (!u || !isVerified(u)) {
        setState({ user: u, access: "out", loading: false });
        return;
      }
      // Emulator hatch: force the `pending` branch locally so the join→rules→PendingScreen
      // seam is drivable in the emulator (isAllowlisted itself short-circuits true under
      // VITE_USE_EMULATORS, so this is the only way to reach `pending` there).
      if (import.meta.env.VITE_FORCE_PENDING === "1") {
        setState({ user: u, access: "pending", loading: false });
        void joinWaitlist(u).catch((e) => console.warn("[waitlist] join failed", e));
        return;
      }
      // RESTORED sessions re-pass the allowlist too. Allowlisted → in. Verified-but-not →
      // first-class `pending` (never logged out) + enqueue on the waitlist (fire-and-forget).
      void isAllowlisted(u.email).then((ok) => {
        if (my !== seq) return; // superseded by a newer auth event — never join on a stale event
        if (ok) {
          setState({ user: u, access: "in", loading: false });
          return;
        }
        setState({ user: u, access: "pending", loading: false });
        void joinWaitlist(u).catch((e) => console.warn("[waitlist] join failed", e));
      });
    });
  }, []);
  return <AuthCtx.Provider value={{ ...state, corp: state.access === "in" }}>{children}</AuthCtx.Provider>;
}
